import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ArrowLeft, CheckCircle2 } from "lucide-react";
import Navigation from "../../components/sections/Navigation";
import { Footer } from "../../components";
import Button from "../../components/ui/Button";

const Terms = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.documentElement.classList.add("dark");
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, []);

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: `By accessing and using FaceTraits ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.`,
    },
    {
      title: "2. Description of Service",
      content: `FaceTraits provides AI-powered facial analysis services, including personality insights, posture analysis, and style recommendations. The Service is provided "as is" and we reserve the right to modify, suspend, or discontinue any part of the Service at any time.`,
    },
    {
      title: "3. User Accounts",
      content: `To access certain features of the Service, you may be required to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.`,
    },
    {
      title: "4. Privacy and Data Protection",
      content: `We are committed to protecting your privacy. Your images are processed securely and we do not store your photos after analysis. Please review our Privacy Policy for detailed information about how we collect, use, and protect your personal information.`,
    },
    {
      title: "5. Subscription Plans and Billing",
      content: `FaceTraits offers multiple subscription plans with different features and usage limits. By subscribing to a paid plan, you agree to pay the applicable fees. Subscriptions are billed on a monthly or annual basis, as selected. You may cancel your subscription at any time, and cancellation will take effect at the end of your current billing period.`,
    },
    {
      title: "6. Free Trial and Refunds",
      content: `We offer a free tier that allows limited use of the Service. For paid subscriptions, refunds are available within 14 days of initial purchase if you are not satisfied with the Service. Refund requests must be submitted through our support channels.`,
    },
    {
      title: "7. Usage Limits and Restrictions",
      content: `Each subscription plan has specific usage limits (e.g., number of analyses per day). You agree not to exceed these limits or attempt to circumvent them. We reserve the right to suspend or terminate accounts that violate these restrictions.`,
    },
    {
      title: "8. Intellectual Property",
      content: `All content, features, and functionality of the Service, including but not limited to text, graphics, logos, and software, are the property of FaceTraits and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.`,
    },
    {
      title: "9. User Content",
      content: `You retain all rights to any images or content you upload to the Service. By uploading content, you grant us a limited license to process and analyze your content solely for the purpose of providing the Service. We do not claim ownership of your content.`,
    },
    {
      title: "10. Prohibited Uses",
      content: `You agree not to use the Service:
- For any illegal purpose or in violation of any laws
- To upload content that infringes on the rights of others
- To attempt to reverse engineer or extract our AI models
- To use automated systems to access the Service in a manner that exceeds reasonable usage
- To interfere with or disrupt the Service or servers
- To impersonate any person or entity`,
    },
    {
      title: "11. Disclaimers",
      content: `THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. We do not guarantee the accuracy, completeness, or usefulness of any analysis or insights provided by the Service. The Service is for informational purposes only and should not be used as the sole basis for important decisions.`,
    },
    {
      title: "12. Limitation of Liability",
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, FACEAI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.`,
    },
    {
      title: "13. Indemnification",
      content: `You agree to indemnify and hold harmless FaceTraits, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of your use of the Service or violation of these Terms.`,
    },
    {
      title: "14. Termination",
      content: `We may terminate or suspend your account and access to the Service immediately, without prior notice, for any reason, including if you breach these Terms. Upon termination, your right to use the Service will cease immediately.`,
    },
    {
      title: "15. Changes to Terms",
      content: `We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.`,
    },
    {
      title: "16. Governing Law",
      content: `These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which FaceTraits operates, without regard to its conflict of law provisions.`,
    },
  ];

  return (
    <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans">
      <Navigation />

      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto relative">
          {/* Header */}
          <div className="mb-8">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="mb-6 flex items-center gap-2 text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>

            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-teal-500/20 text-teal-400">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-2 bg-gradient-to-r from-zinc-100 via-teal-100 to-rose-100 bg-clip-text text-transparent">
                  Terms and Conditions
                </h1>
                <p className="text-zinc-400 text-sm">
                  Last Updated:{" "}
                  {new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-teal-500/10 border border-teal-500/20 mb-8">
              <p className="text-sm text-zinc-300">
                Please read these Terms and Conditions carefully before using
                our Service. By accessing or using FaceTraits, you agree to be
                bound by these Terms.
              </p>
            </div>
          </div>

          {/* Terms Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={index}
                className="rounded-xl p-6 sm:p-8 border border-zinc-800 bg-stone-900/30 hover:border-zinc-700 transition-colors"
              >
                <h2 className="text-2xl font-bold mb-4 text-teal-400 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  {section.title}
                </h2>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-line">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center">
            <p className="text-sm text-zinc-500">
              If you have any questions about these Terms, please{" "}
              <button
                onClick={() => {
                  if (window.Tawk_API && window.Tawk_API.maximize) {
                    window.Tawk_API.maximize();
                  }
                }}
                className="text-teal-400 hover:text-teal-300 underline"
              >
                contact our support team
              </button>
              .
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Terms;
